import { Vendor, ListingStatus, ApiResult } from '../../../types';
import { api } from '../../d1';
import { VendorMapper } from '../mappers/vendor.mapper';
import { isUnverifiedSeedVendor } from '../../../utils/vendorQuality.mjs';

export class D1VendorRepository {
    async getVendorById(id: string): Promise<Vendor | undefined> {
        try {
            const row = await api.vendors.get(id);
            if (!row) return undefined;
            const vendor = VendorMapper.toDomain(row);
            // Seed vendor (owner_id === 'system_curated') — never serve publicly.
            if (isUnverifiedSeedVendor({ owner_id: vendor.seller?.id })) return undefined;
            return vendor;
        } catch (e) {
            console.error('D1 getVendorById error', e);
            return undefined;
        }
    }

    async getVendors(filters?: any): Promise<Vendor[]> {
        try {
            const params: any = {};
            if (filters) {
                if (filters.category && filters.category !== 'all') params.category = filters.category;
                if (filters.query) params.query = filters.query;
                if (filters.status) params.status = filters.status;
                if (filters.admin) params.admin = 'true';
            }

            const rows = await api.vendors.list(params);
            const allVendors = rows.map((r: any) => VendorMapper.toDomain(r));

            // Základný status filter, ak je to pre public — nevyhadzaj seed dodávateľov
            // (owner_id === 'system_curated'), kvoli riziku spam-policy Google.
            const isPublic = !filters?.admin;
            if (isPublic) {
                const filtered = allVendors.filter(v => !isUnverifiedSeedVendor(v));
                return filtered.filter(v => v.status === ListingStatus.APPROVED || !v.status);
            }
            return allVendors;
        } catch (e) {
            console.error('D1 getVendors error', e);
            return [];
        }
    }

    async getMyVendors(userId: string): Promise<Vendor[]> {
        try {
            const rows = await api.vendors.list({ owner_id: userId, status: 'all' });
            return rows.map((r: any) => VendorMapper.toDomain(r));
        } catch { return []; }
    }

    async addVendor(vendor: Vendor, userId: string): Promise<ApiResult<Vendor>> {
        try {
            const row = VendorMapper.toRow(vendor, userId);
            const res = await api.vendors.upsert(row);
            if (res.success || res.id) return { success: true, data: vendor };
            return { success: false, error: res.error || 'Failed to add vendor' };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    }

    async updateVendor(vendor: Vendor, userId: string): Promise<ApiResult<Vendor>> {
        try {
            const row = VendorMapper.toRow(vendor, userId);
            const res = await api.vendors.upsert(row);
            if (res.success || res.id) return { success: true, data: vendor };
            return { success: false, error: res.error || 'Failed to update vendor' };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    }

    async deleteVendor(id: string): Promise<ApiResult<void>> {
        try {
            await api.vendors.remove(id);
            return { success: true, data: undefined };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    }
}
